import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Checkbox, Form, Input, Modal, Radio, Select } from 'antd';
import { CardBadge } from '../CardBadge';
import { appConfig } from '../../../../settings';
import {
    IGetOrder,
    IPackage,
    IPostOrderAttributes,
    ISelectList,
    ITariff,
    ITariffPackagesToBuyItem,
    NotificationType,
} from '../../../../interfaces';
import {
    useCardSetsSelect,
    useCardsSelect,
    useCompaniesChildrenSelect,
    useProfile,
    useProfileIsRight,
} from '../../../../hooks';
import {
    addNotification,
    apiAddFormErrorMessages,
    apiMessagesGetFormRules,
    apiMessagesSaveMessages,
    checkOutOrder,
    cleanApiMessages,
    getCardSetCombinationCard,
    getCardSetsSelect,
    getCompaniesChildrenSelect,
    postOrder,
} from '../../../../middleware';
import { tB, tF, tI, tT } from '../../../../translate';
import TariffsStyles from '../../m_styles.less';

interface IAssignTariffModal {
    isOpen: boolean;
    closeStepper: () => void;
    tariff?: ITariff;
    tariffPackage?: IPackage | ITariffPackagesToBuyItem;
}

export const AssignTariff: React.FC<IAssignTariffModal> = observer(
    ({ tariff, closeStepper, tariffPackage }: IAssignTariffModal) => {
        if (!tariff && !tariffPackage) {
            console.error('Either tariff or tariffPackage must be provided');
            return null;
        }

        const [form] = Form.useForm();
        const [targetType, setTargetType] = useState<string>('cards');
        const [selectedTarget, setSelectedTarget] = useState<string | undefined>(undefined);
        const [currentCompany, setCurrentCompany] = useState<string>();
        const [isSending, setSending] = useState(false);
        const [isValid, setValid] = useState(false);
        const isLord = useProfileIsRight('LORD');
        const { companyId = '' } = useProfile();

        const isTariff = !!tariff;
        const item = tariff || tariffPackage;

        const { companies = [], priceCents = 0, id = '', name = '', description = '' } = (item || {}) as any;
        const itemCompanyId = (item as any)?.companyId?.id || (item as any)?.companyId;

        const isSeller = isLord || +itemCompanyId === +companyId;

        const cardSetSelect = useCardSetsSelect().map((cardSet: any) => ({
            label: cardSet.name,
            value: cardSet.id,
            id: cardSet.id,
        })) as never as ISelectList;

        const cardsSelect = useCardsSelect().map((cards: any) => ({
            label: cards.name,
            value: cards.id,
            id: cards.id,
        })) as never as ISelectList;

        const selectList = useCompaniesChildrenSelect();

        const companySelect = (!isTariff ? selectList : companies).map((company: any) => ({
            label: company.name,
            value: company.id,
            id: company.id,
        })) as never as ISelectList;

        // const companySelect = companies?.map((company: any) => ({
        //     label: company.name,
        //     value: company.id,
        //     id: company.id,
        // })) as never as ISelectList;

        const handleTargetTypeChange = (e: any) => {
            setTargetType(e.target.value);
            setSelectedTarget(undefined);
            form.setFieldValue('target', undefined);
        };

        const getSelectPlaceholder = () => {
            switch (targetType) {
                case 'cards':
                    return 'Select cards';
                case 'card-sets':
                    return 'Select card sets';
                case 'card-set-pool':
                    return 'Select card set pool';
                default:
                    return 'Select target';
            }
        };

        const getSelectOptions = () => {
            switch (targetType) {
                case 'cards':
                    return cardsSelect;
                case 'card-sets':
                    return cardSetSelect;
                case 'card-set-pool':
                    return cardSetSelect;
                default:
                    return [];
            }
        };

        const onFieldsChange = async (): Promise<void> => {
            try {
                await form.validateFields();
                setValid(true);
            } catch (error: any) {
                const hasErrors = error?.errorFields?.some((field: any) => field.errors.length > 0);
                setValid(!hasErrors);
            }
        };

        const checkoutFlow = async (ordersId?: string) => {
            if (ordersId) {
                const { isError, errors = [] } = await checkOutOrder(ordersId);

                if (isError) {
                    addNotification({
                        type: NotificationType.ERROR,
                        message: 'Assignment Error',
                        description: 'there was an error while checking out the order',
                        duration: 5,
                        isUsersError: true,
                    });

                    cleanApiMessages();
                    apiMessagesSaveMessages(errors);
                    await form.validateFields();
                } else {
                    // const respData = (respCheckOut || {}) as IResponsePostCheckOutDataAPI;
                    // if (respData?.order?.metadata?.payment_url) {
                    //     window.open(respData?.order?.metadata?.payment_url as string, '_blank');
                    // }

                    // if (isAvailablePage('OrderView') && ordersId) {
                    //     await routerStore.goTo('OrderView', { params: { id: ordersId } });
                    // }

                    addNotification({
                        type: NotificationType.SUCCESS,
                        message: isTariff ? 'Tariff successfully assigned' : 'Tariff package successfully assigned',
                        duration: 5,
                        isTranslated: false,
                    });

                    closeStepper();
                }
            } else {
                console.warn('Not received order Id');
            }
        };

        const onFinishEditOrderCompany = async (): Promise<void> => {
            setSending(true);

            const orderables = appConfig.orderables;
            const paymentMethods = appConfig.paymentMethods;

            const target = form.getFieldValue('target');

            const attributes: IPostOrderAttributes = {
                amount: +priceCents,
                description: form.getFieldValue('description'),
                orderable_id: id,
                orderable_type: isTariff ? orderables.tariff : orderables.tariffPackage,
                payment_type: paymentMethods.crediting,
                metadata: {},
            };

            if (targetType === 'cards' && attributes.metadata) {
                attributes.metadata.card_ids = [...target];
            } else if (targetType === 'card-sets' && attributes.metadata) {
                attributes.metadata.card_set_ids = [...target];
            } else if (targetType === 'card-set-pool' && attributes.metadata) {
                attributes.metadata.card_set_id = target;
            } else {
                attributes.company_id = companyId;
            }

            if (form.getFieldValue('renewable') && !isTariff && attributes.metadata) {
                attributes.metadata.renewable = form.getFieldValue('renewable');
            }

            if (isSeller) {
                attributes.company_id = currentCompany;
            }

            const checkoutCompany = isSeller ? currentCompany : companyId;

            attributes.company_id = checkoutCompany;

            const { isError, resp } = await postOrder(checkoutCompany || '', attributes);

            if (isError) {
                const errorResp = resp as any;
                if (errorResp?.errors?.base) {
                    addNotification({
                        type: NotificationType.ERROR,
                        message: 'Assignment Error',
                        description: errorResp.errors.base.join(', '),
                        duration: 5,
                        isUsersError: true,
                    });
                }

                apiAddFormErrorMessages(resp || {}, form);
                await form.validateFields();
            } else {
                const ordersId = (resp as IGetOrder).order.id;

                await checkoutFlow(ordersId);
            }

            setSending(false);
        };

        useEffect(() => {
            if (isSeller) {
                getCompaniesChildrenSelect(itemCompanyId, true);
            }
        }, []);

        useEffect(() => {
            if (isSeller && currentCompany) {
                getCardSetsSelect({ companyId: currentCompany });
                getCardSetCombinationCard(currentCompany);
            } else if (!isSeller) {
                getCardSetsSelect({ companyId });
                getCardSetCombinationCard(companyId);
            }
        }, [currentCompany, isSeller, companyId]);

        useEffect(() => {
            onFieldsChange();
        }, [targetType, selectedTarget]);

        const isDisabled = !selectedTarget && targetType !== 'company';

        return (
            <>
                <p>
                    {tT(
                        isTariff
                            ? 'Buy a tariff for a card, card set, or all cards in a company'
                            : 'Buy a tariff package for a card, card set, or all cards in a company',
                    )}
                </p>

                <div className={TariffsStyles.assignTariffFormContainer}>
                    <Form
                        form={form}
                        name="assignTariff"
                        onFinish={onFinishEditOrderCompany}
                        onChange={onFieldsChange}
                        scrollToFirstError={true}
                        layout={'vertical'}
                        className={'formStyle'}
                    >
                        <div>
                            <p className={TariffsStyles.title}>{tF(isTariff ? 'Tariff' : 'Tariff Package')}</p>
                            <div className={TariffsStyles.header}>
                                <div className={TariffsStyles.nameContainer}>
                                    <h3 className={TariffsStyles.name}>{name}</h3>
                                    <CardBadge name={tI('Selected')} />
                                </div>
                                <p>{description}</p>
                            </div>

                            <p className={TariffsStyles.buyFor}>{tI('Buy For')}</p>

                            {isSeller && (
                                <Form.Item name="buyFor" label={tF('Company')}>
                                    <Select
                                        showSearch
                                        filterOption={(input, option: any) =>
                                            option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                                            option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        placeholder={tF('Choose a company to buy for')}
                                        className={TariffsStyles.width100}
                                        options={companySelect}
                                        value={currentCompany}
                                        onChange={(value) => {
                                            setCurrentCompany(value);
                                            form.setFieldValue('target', []);
                                        }}
                                    />
                                </Form.Item>
                            )}

                            <Radio.Group
                                onChange={handleTargetTypeChange}
                                value={targetType}
                                className={TariffsStyles.radioGroup}
                            >
                                <Radio value="cards" className={TariffsStyles.item}>
                                    {tF('Cards')}
                                </Radio>
                                <Radio value="card-sets" className={TariffsStyles.item}>
                                    {tF('Card sets (all cards in sets)')}
                                </Radio>
                                {!isTariff ? (
                                    <Radio value="card-set-pool" className={TariffsStyles.item}>
                                        {tF('Card set (Pool)')}
                                    </Radio>
                                ) : null}
                                <Radio value="company" className={TariffsStyles.item}>
                                    {tF('All Cards in Company')}
                                </Radio>
                            </Radio.Group>

                            {targetType !== 'company' && (
                                <Form.Item name="target">
                                    <Select
                                        showSearch
                                        filterOption={(input, option: any) =>
                                            option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                                            option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        mode={targetType !== 'card-set-pool' ? 'multiple' : undefined}
                                        placeholder={getSelectPlaceholder()}
                                        value={selectedTarget}
                                        onChange={(value) => {
                                            setSelectedTarget(value);
                                            form.setFieldValue('target', value);
                                        }}
                                        className={TariffsStyles.width100}
                                        options={getSelectOptions()}
                                    />
                                </Form.Item>
                            )}
                            {!isTariff ? (
                                <Form.Item name="renewable" rules={apiMessagesGetFormRules} valuePropName="checked">
                                    <Checkbox>{tF('Renewable Package')}</Checkbox>
                                </Form.Item>
                            ) : null}
                            <Form.Item name="description" label={tF('Description (Optional)')}>
                                <Input.TextArea placeholder={tT('Add a description for this assignment')} rows={3} />
                            </Form.Item>
                        </div>

                        <div className={TariffsStyles.toolBar}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                className={TariffsStyles.singleButton}
                                disabled={isSending || !isValid || isDisabled}
                                loading={isSending}
                            >
                                {tI(
                                    isSending
                                        ? `Processing Assignment...`
                                        : `Buy ${isTariff ? 'Tariff' : 'Tariff Package'}`,
                                )}
                            </Button>
                        </div>
                    </Form>
                </div>
            </>
        );
    },
);

export function AssignTariffModal({ isOpen, closeStepper, tariff, tariffPackage }: IAssignTariffModal) {
    const isTariff = !!tariff;

    const handleModalCancel = () => {
        Modal.confirm({
            title: tT('Close Dialog?'),
            content: tT('Are you sure you want to close this dialog? Any unsaved changes will be lost.'),
            okText: tB('Yes'),
            cancelText: tB('No'),
            onOk() {
                closeStepper();
            },
            wrapClassName: 'modal',
        });
    };

    return (
        <Modal
            title={tI(`Buy ${isTariff ? 'Tariff' : 'Tariff Package'}`)}
            open={isOpen}
            onCancel={handleModalCancel}
            wrapClassName={'modal'}
            footer={null}
            destroyOnClose
        >
            <AssignTariff tariff={tariff} isOpen={isOpen} closeStepper={closeStepper} tariffPackage={tariffPackage} />
        </Modal>
    );
}
